import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useSiteConfig, whatsappHref } from "@/lib/site-config";

/**
 * Floating WhatsApp button — fixed to the bottom-right corner of every
 * page. Clicking opens WhatsApp with a pre-written enquiry message.
 *
 * On phones the button is smaller and steps out of the way while the
 * reader scrolls down or types into a form (returning on scroll-up), so
 * it never sits on top of a submit button or a line of copy the reader is
 * actually looking at. It also rises above the compare tray and respects
 * the device safe area via `.floating-anchor`.
 *
 * Hidden if the WhatsApp number isn't configured on the server.
 */
export function FloatingWhatsApp() {
  const siteConfig = useSiteConfig();
  const retreated = useMobileRetreat();
  if (!siteConfig.whatsapp.set) return null;

  const message = encodeURIComponent(
    "Hi Hive Homes, I'd like to enquire about a property.",
  );
  const href = `${whatsappHref(siteConfig.whatsapp)}?text=${message}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with Hive Homes on WhatsApp"
      aria-hidden={retreated || undefined}
      tabIndex={retreated ? -1 : undefined}
      className={cn(
        "group floating-anchor fixed z-40 inline-flex size-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_10px_28px_rgba(37,211,102,0.45)] transition-[transform,opacity,box-shadow] duration-300 hover:scale-110 hover:shadow-[0_14px_36px_rgba(37,211,102,0.55)] sm:size-16 short:size-12",
        // Slide off the bottom edge on phones only (portrait or landscape);
        // tablets and desktops never move.
        retreated &&
          "max-sm:pointer-events-none max-sm:translate-y-[calc(100%+2rem)] max-sm:opacity-0 short:pointer-events-none short:translate-y-[calc(100%+2rem)] short:opacity-0",
      )}
    >
      {/* animate-ping scales the halo to 2x, so without pointer-events-none
          it hit-tests far outside the visible button and steals clicks from
          nearby footer links (e.g. "Terms of Use" on laptop widths). The
          halo is a desktop flourish; on a phone it doubles the footprint
          over content, so it only renders from `sm` up. */}
      <span aria-hidden className="pointer-events-none absolute inset-0 hidden rounded-full bg-[#25D366] opacity-60 animate-ping sm:block short:hidden" />
      <svg
        viewBox="0 0 32 32"
        className="relative z-10 size-6 sm:size-8 short:size-6"
        fill="currentColor"
        aria-hidden
      >
        <path d="M16.004 3C9.376 3 4 8.376 4 15.004c0 2.114.553 4.18 1.604 6L4 27l6.16-1.6a11.96 11.96 0 0 0 5.844 1.494h.005C22.628 26.894 28 21.518 28 14.89A11.91 11.91 0 0 0 24.49 6.51 11.92 11.92 0 0 0 16.004 3Zm0 21.795h-.004a9.93 9.93 0 0 1-5.064-1.39l-.363-.215-3.65.948.974-3.555-.236-.376a9.92 9.92 0 0 1-1.512-5.207c0-5.475 4.456-9.931 9.932-9.931 2.652 0 5.143 1.034 7.018 2.91a9.86 9.86 0 0 1 2.91 7.022c0 5.476-4.46 9.794-10.005 9.794Zm5.448-7.434c-.299-.149-1.765-.871-2.038-.97-.273-.099-.472-.149-.671.149-.198.298-.77.97-.943 1.168-.174.198-.348.224-.646.075-.298-.15-1.26-.464-2.4-1.479-.887-.791-1.486-1.768-1.66-2.066-.174-.298-.018-.46.131-.609.135-.134.299-.348.448-.522.149-.174.198-.298.298-.497.099-.198.05-.373-.025-.522-.075-.149-.671-1.617-.92-2.214-.243-.582-.49-.502-.671-.512l-.572-.01a1.1 1.1 0 0 0-.795.373c-.273.298-1.043 1.02-1.043 2.486 0 1.466 1.068 2.882 1.218 3.082.149.198 2.102 3.21 5.094 4.504.712.308 1.267.491 1.7.628.715.227 1.365.195 1.879.118.573-.086 1.765-.722 2.014-1.42.249-.696.249-1.293.174-1.42-.075-.124-.273-.198-.572-.347Z" />
      </svg>
    </a>
  );
}

const TEXT_FIELD = /^(INPUT|TEXTAREA|SELECT)$/;
const SCROLL_THRESHOLD = 8;

/** Portrait phone, or a phone held sideways (mirrors the `short:` variant). */
const PHONE_QUERY =
  "(max-width: 639px), ((max-height: 500px) and (orientation: landscape))";

/**
 * True while the button should step out of the way on a phone: the reader
 * is scrolling down, or a text field has focus (the on-screen keyboard
 * halves the viewport and the button would land on the form). Always
 * false on tablets and desktops, so that button is never marked hidden.
 */
function useMobileRetreat() {
  const [retreated, setRetreated] = useState(false);

  useEffect(() => {
    const phone = window.matchMedia(PHONE_QUERY);
    let lastY = window.scrollY;
    let frame = 0;
    let scrollingDown = false;
    let typing = false;
    const apply = () => setRetreated(phone.matches && (scrollingDown || typing));

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        const delta = y - lastY;
        if (Math.abs(delta) < SCROLL_THRESHOLD) return;
        lastY = y;
        // Rubber-banding past the top reports negative scroll positions.
        scrollingDown = delta > 0 && y > 0;
        apply();
      });
    };
    const onFocusIn = (e: FocusEvent) => {
      typing = TEXT_FIELD.test((e.target as Element | null)?.tagName ?? "");
      apply();
    };
    const onFocusOut = () => {
      typing = false;
      apply();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    phone.addEventListener("change", apply);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      phone.removeEventListener("change", apply);
    };
  }, []);

  return retreated;
}
